// Comprehensive dosage forms for enhanced search - based on actual Clinigen product data
// This list includes all unique formulations found in your Azure AI Search index
export const COMPREHENSIVE_DOSAGE_FORMS = [
  // Core tablet forms (most common in your data)
  'tablet',
  'tablets',
  'pill',
  'pills',
  'film-coated tablet',
  'film coated tablet',
  'enteric-coated tablet',
  'enteric coated tablet',
  'extended-release tablet',
  'extended release tablet',
  'prolonged-release tablet',
  'prolonged release tablet',
  'effervescent tablet',

  // Capsules
  'capsule',
  'capsules',

  // Injectable forms (high volume in your catalog)
  'injection',
  'injections',
  'solution for injection',
  'powder for injection',
  'suspension for injection',
  'lyophilisate for injection',
  'lyophilisate',

  // Solutions
  'solution',
  'solutions',
  'oral solution',
  'topical solution',
  'rectal solution',
  'solution for infusion',
  'concentrate for solution',
  'concentrate',
  'conc',

  // Suspensions
  'suspension',
  'suspensions',
  'oral suspension',

  // Powders
  'powder',
  'powders',
  'powder for solution',
  'granules',
  'granules for solution',
  'granules for oral solution',

  // Specialized forms from your data
  'drops',
  'drop',
  'eye drops',
  'patch',
  'patches',
  'transdermal patch',
  'cream',
  'gel',
  'ointment',
  'lotion',
  'spray',
  'inhaler',
  'suppository',
  'enema',
  'film',
  'syrup',
  'liquid',
  'pessary',

  // Packaging/route descriptors found in your data
  'vial',
  'vials',
  'bottle',
  'bottles',
  'blister',
  'syringe',
  'pre-filled syringe',
  'prefilled syringe',

  // Route-specific (helps with precise search)
  'oral',
  'topical',
  'rectal',
  'eye',
  'nasal',
  'transdermal',
  'intra uterine',
  'iud',

  // Additional specific forms from your product data
  'effervescent',
  'enteric',
  'extended',
  'prolonged',
  'coated',
  'device',
  'infusion',
  'solvent'
] as const

/**
 * Find partial matches in dosage forms
 * @param word - The word to find partial matches for
 * @returns The matching dosage form or null if no match found
 */
export const findPartialDosageFormMatch = (word: string): string | null => {
  const wordLower = word.toLowerCase()

  // First try exact match
  const exactMatch = COMPREHENSIVE_DOSAGE_FORMS.find(
    (form) => form.toLowerCase() === wordLower
  )
  if (exactMatch) {
    return exactMatch
  }

  // Then try partial matches - find forms that contain the word or vice versa
  const partialMatch = COMPREHENSIVE_DOSAGE_FORMS.find((form) => {
    const formLower = form.toLowerCase()
    // Match if word is contained in form (e.g., "table" in "tablet")
    // or if word contains form (e.g., "tablets" contains "tablet")
    return formLower.includes(wordLower) || wordLower.includes(formLower)
  })

  return partialMatch || null
}

/**
 * Parse search query and extract potential dosage form
 * @param searchString - The search string to parse
 * @param isExactMatch - Whether this is for an exact match scenario
 * @returns Object containing the product name and dosage form
 */
export const parseSearchQuery = (
  searchString: string,
  isExactMatch: boolean = false
): { productName: string; dosageForm: string } => {
  if (!searchString || searchString.trim() === '') {
    return { productName: '', dosageForm: '' }
  }

  const cleanQuery = searchString.trim().toLowerCase()
  const queryWords = cleanQuery.split(/\s+/)

  // For exact matches, treat second word (and beyond) as dosage form filter
  // ONLY if it's a valid dosage form
  if (isExactMatch && queryWords.length >= 2) {
    const productName = queryWords[0]
    const potentialDosageForm = queryWords.slice(1).join(' ')

    // Check if the potential dosage form is actually valid
    const isValidDosageForm = COMPREHENSIVE_DOSAGE_FORMS.some(
      (form) =>
        form.toLowerCase() === potentialDosageForm.toLowerCase() ||
        form.toLowerCase().includes(potentialDosageForm.toLowerCase()) ||
        potentialDosageForm.toLowerCase().includes(form.toLowerCase())
    )

    if (isValidDosageForm) {
      return {
        productName: productName,
        dosageForm: potentialDosageForm
      }
    } else {
      // If not a valid dosage form, treat the entire query as product name
      return {
        productName: cleanQuery,
        dosageForm: ''
      }
    }
  }

  // For non-exact matches, try exact word-based matching for multi-word dosage forms first
  for (const form of COMPREHENSIVE_DOSAGE_FORMS) {
    const formLower = form.toLowerCase()
    const formWords = formLower.split(/\s+/)

    // For multi-word forms, check if all words appear consecutively
    if (formWords.length > 1) {
      for (let i = 0; i <= queryWords.length - formWords.length; i++) {
        const wordSlice = queryWords.slice(i, i + formWords.length).join(' ')
        if (wordSlice === formLower) {
          const productWords = [
            ...queryWords.slice(0, i),
            ...queryWords.slice(i + formWords.length)
          ]
          const productName = productWords.join(' ').trim()
          return {
            productName: productName || cleanQuery,
            dosageForm: form
          }
        }
      }
    }
  }

  // Try exact single-word matching
  for (const word of queryWords) {
    const exactMatch = COMPREHENSIVE_DOSAGE_FORMS.find(
      (form) => form.toLowerCase() === word
    )
    if (exactMatch) {
      const productWords = queryWords.filter((w) => w !== word)
      const productName = productWords.join(' ').trim()
      return {
        productName: productName || cleanQuery,
        dosageForm: exactMatch
      }
    }
  }

  // Try partial matching for single words
  for (const word of queryWords) {
    const partialMatch = findPartialDosageFormMatch(word)
    if (partialMatch) {
      const productWords = queryWords.filter((w) => w !== word)
      const productName = productWords.join(' ').trim()
      return {
        productName: productName || cleanQuery,
        dosageForm: partialMatch
      }
    }
  }

  // No dosage form detected - return the full query as product name
  return { productName: cleanQuery, dosageForm: '' }
}

/**
 * Extract the product name from a search query for server requests
 * This is specifically for ProductSearch to send only the product name to the server
 * while preserving the dosage form information for client-side filtering
 * @param searchString - The search string to parse
 * @returns Object containing the query to send to server and the detected dosage form
 */
export const extractProductNameForSearch = (
  searchString: string
): { serverQuery: string; dosageForm: string } => {
  const parsed = parseSearchQuery(searchString, false)

  // If we detected a dosage form, send only the product name to the server
  if (parsed.dosageForm && parsed.productName) {
    return {
      serverQuery: parsed.productName,
      dosageForm: parsed.dosageForm
    }
  }

  // If no dosage form detected, send the full query
  return {
    serverQuery: searchString.trim(),
    dosageForm: ''
  }
}
